import { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { C, RADIUS, SHADOW, TYPE } from './tokens';
import StateChip from './StateChip';

/**
 * The one container for a group of related settings.
 *
 * Merchant Setup previously used three different containers for the same job:
 * Collapse + a bespoke SectionHeader (merchant details -> Configuration tab),
 * MUI Accordion (Admin -> "Routing Rules"), and bare Card + CardHeader
 * (everywhere else). This is the accordion pattern, which already had the best
 * affordances, standardised -- plus the two things none of them had: a state
 * chip and a count, so you can tell what is configured without opening it.
 *
 * @param title      short, plain-English name of the group
 * @param subtitle   one line on what these settings control
 * @param icon       optional leading glyph
 * @param state      CONFIG_STATE key -- summary state for the whole group
 * @param stateHint  tooltip explaining the state
 * @param badge      optional right-aligned text (e.g. "4 of 6 set")
 * @param defaultOpen
 */
export default function ConfigSection({
    title, subtitle, icon, state, stateHint, badge,
    defaultOpen = false, children, id,
}) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Box id={id} sx={{
            bgcolor: C.surface,
            border: `1px solid ${C.line}`,
            borderRadius: RADIUS.md,
            boxShadow: open ? SHADOW.raised : SHADOW.card,
            overflow: 'hidden',
            mb: 1.5,
            transition: 'box-shadow .18s ease',
        }}>
            <Box
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); }
                }}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none',
                    bgcolor: open ? C.surfaceSunken : C.surface,
                    borderBottom: open ? `1px solid ${C.lineSoft}` : 'none',
                    '&:hover': { bgcolor: C.surfaceSunken },
                    '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: -2 },
                }}
            >
                {icon && (
                    <Box sx={{
                        width: 30, height: 30, flexShrink: 0,
                        display: 'grid', placeItems: 'center',
                        borderRadius: RADIUS.sm,
                        bgcolor: C.accentSoft, color: C.accent,
                        '& svg': { fontSize: 17 },
                    }}>{icon}</Box>
                )}

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography sx={TYPE.sectionTitle}>{title}</Typography>
                        {state && <StateChip state={state} hint={stateHint} />}
                    </Box>
                    {subtitle && (
                        <Typography sx={{ ...TYPE.rowHelp, mt: 0.25 }}>{subtitle}</Typography>
                    )}
                </Box>

                {badge && (
                    <Typography sx={{ ...TYPE.meta, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                        {badge}
                    </Typography>
                )}

                <ExpandMoreIcon sx={{
                    flexShrink: 0, fontSize: 20, color: C.inkGhost,
                    transform: open ? 'rotate(180deg)' : 'none',
                    transition: 'transform .18s ease',
                }} />
            </Box>

            <Collapse in={open} unmountOnExit>
                <Box sx={{ px: 2, py: 0.5 }}>{children}</Box>
            </Collapse>
        </Box>
    );
}
